import { useMemo } from 'react';
import styled from 'styled-components';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { formatNumberToCurrency } from '../../../utils';
import { TradeCustomersResponse } from '../api/tradeApi';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type Props = {
  data: TradeCustomersResponse['monthly_revenue'];
  currency: string;
};

const formatMonthLabel = (monthStr: string) => {
  const [y, m] = monthStr.split('-');
  const d = new Date(parseInt(y), parseInt(m) - 1);
  return d.toLocaleString('default', { month: 'short', year: '2-digit' });
};

export const TradeRevenueChart = ({ data, currency }: Props) => {
  const ccy = (currency || 'gbp').toUpperCase();
  const toMajor = (pence: number) => (pence || 0) / 100;

  const chartData = useMemo(
    () => ({
      labels: data.map((p) => formatMonthLabel(p.month)),
      datasets: [
        {
          label: 'Your commission',
          data: data.map((p) => toMajor(p.commission)),
          borderColor: '#0F9D58',
          backgroundColor: 'rgba(15, 157, 88, 0.15)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 4
        }
      ]
    }),
    [data]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top', labels: { boxWidth: 12, usePointStyle: true } },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${formatNumberToCurrency(ctx.parsed.y, ccy)}`
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value) => formatNumberToCurrency(Number(value), ccy)
          }
        }
      }
    }),
    [ccy]
  );

  if (!data || data.length === 0) {
    return (
      <EmptyChart>
        No commission yet — this chart will fill in as your customers are billed.
      </EmptyChart>
    );
  }

  return (
    <ChartWrap>
      <Line data={chartData} options={options} />
    </ChartWrap>
  );
};

const ChartWrap = styled.div`
  position: relative;
  height: 18rem;
  width: 100%;
`;

const EmptyChart = styled.div`
  padding: 2.5rem 1.25rem;
  text-align: center;
  color: #667085;
`;
